import { HumanName, Patient } from "@medplum/fhirtypes";

/**
 * Returns a copy of `name` with leading/trailing whitespace removed from string fields.
 * Empty `given` parts are dropped after trimming.
 * Does not add optional keys (e.g. prefix/suffix) when they were absent — avoids spurious
 * `undefined` properties that break deep equality with API payloads.
 */
function trimHumanName(name: HumanName): HumanName {
  const result: HumanName = { ...name };

  if (name.family !== undefined) {
    result.family = name.family.trim();
  }

  if (name.given !== undefined) {
    const givenTrimmed = name.given.map((part) => part.trim()).filter((part) => part.length > 0);
    if (givenTrimmed.length > 0) {
      result.given = givenTrimmed;
    } else {
      delete (result as { given?: string[] }).given;
    }
  }

  if (name.text !== undefined) {
    result.text = name.text.trim();
  }

  if (name.prefix !== undefined) {
    result.prefix = name.prefix.map((prefix) => prefix.trim());
  }

  if (name.suffix !== undefined) {
    result.suffix = name.suffix.map((suffix) => suffix.trim());
  }

  return result;
}

/**
 * Returns a copy of the patient with trimmed whitespace on demographic string fields
 * (name, telecom, address, identifier, emergency contact names) so downstream systems
 * such as Health Gorilla receive normalized values.
 */
export function trimPatientDemographics<T extends Patient>(patient: T): T {
  const next = { ...patient } as T;

  if (patient.name !== undefined) {
    next.name = patient.name.map(trimHumanName);
  }
  if (patient.telecom !== undefined) {
    next.telecom = patient.telecom.map((telecom) => {
      const row = { ...telecom };
      if (telecom.value !== undefined) {
        row.value = telecom.value.trim();
      }
      return row;
    });
  }
  if (patient.address !== undefined) {
    next.address = patient.address.map((addr) => {
      const row = { ...addr };
      if (addr.line !== undefined) {
        row.line = addr.line.map((line) => line.trim());
      }
      if (addr.city !== undefined) {
        row.city = addr.city.trim();
      }
      if (addr.district !== undefined) {
        row.district = addr.district.trim();
      }
      if (addr.state !== undefined) {
        row.state = addr.state.trim();
      }
      if (addr.postalCode !== undefined) {
        row.postalCode = addr.postalCode.trim();
      }
      if (addr.text !== undefined) {
        row.text = addr.text.trim();
      }
      return row;
    });
  }
  if (patient.identifier !== undefined) {
    next.identifier = patient.identifier.map((identifier) => {
      const row = { ...identifier };
      if (identifier.value !== undefined) {
        row.value = identifier.value.trim();
      }
      return row;
    });
  }
  if (patient.contact !== undefined) {
    next.contact = patient.contact.map((contact) => {
      if (!contact.name) {
        return { ...contact };
      }
      return {
        ...contact,
        name: trimHumanName(contact.name),
      };
    });
  }

  return next;
}
